import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, combineLatest, of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { FormBuilderComponent } from '@/components/form-builder/form-builder.component';
import { DynamicForm, FileRepresentation, SelectOption } from '@/components/form-builder/form-builder.types';
import { getPpmInspectPlanFormObject } from '../utils/ppm-inspect.form-object';

import { PpmService } from '../../ppm.service';
import { PpmMilestonesService } from '../../ppm-milestones.service';
import { CommitteeService } from '@/pages/settings/committee/committee.service';
import { PpmRepository } from '@/store/ppm-state/ppm-state.repository';
import { MultiFormAccordionService } from '@/components/multi-form-accordion/multi-form-accordion.service';
import { ContractingAuthorityService } from '@/pages/settings/contracting-authority/contracting-authority.service';
import { ppmMilestonesStoreFactory } from '@/store/ppm-state/milestones-state/milestones.store';
import { SpinnerComponent } from '@/components/shared/spinner/spinner.component';
import { FileService, NAMESPACE_ENUM } from '@/components/shared/file-list/file-list.service';
import { StatusComponent } from '@/components/shared/status/status.component';
import { BlockBuilderComponent } from '@/components/block-builder/block-builder.component';
import { Ppm, WithMetadata } from '@/types';
import { DynamicBlocks } from '@/components/block-builder/block-builder.types';
import { CommonModule } from '@angular/common';
import { PpmWorkflowBlocksService } from '../../ppm-workflow-blocks.service';
import { Router } from '@angular/router';
import { getPPMPVItems } from '../../utils/ppm-pv-items';
import { PpmMetaDataBlocksService } from '../../ppm-metadata-blocks.service';

@Component({
  selector: 'sm-ppm-inspect-content-block',
  standalone: true,
  imports: [FormBuilderComponent, SpinnerComponent, StatusComponent, BlockBuilderComponent, CommonModule],
  templateUrl: './content-block.component.html',
  styleUrls: ['./content-block.component.scss'],
})
export class PpmInspectContentBlockComponent implements OnInit, OnDestroy {
  /** ── Injected services ─────────────────────────────────────────────── */
  private readonly ppmService = inject(PpmService);
  private readonly ppmMilestonesService = inject(PpmMilestonesService);
  private readonly ppmWorkflowBlocksService = inject(PpmWorkflowBlocksService);
  private readonly ppmMetadataBlocksService = inject(PpmMetaDataBlocksService);
  private readonly committeeService = inject(CommitteeService);
  private readonly ppmRepository = inject(PpmRepository);
  private readonly multiFormAccordionService = inject(MultiFormAccordionService);
  private readonly contractingAuthorityService = inject(ContractingAuthorityService);
  private readonly fileService = inject(FileService);
  private readonly router = inject(Router);

  /** ── Inputs ────────────────────────────────────────────────────────── */
  private _id?: number;
  @Input() set id(value: number | undefined) {
    this._id = value;
    this.tryLoadData();
  }
  get id(): number | undefined {
    return this._id;
  }

  private _version?: number;
  @Input() set version(value: number | undefined) {
    this._version = value;
    this.tryLoadData();
  }
  get version(): number | undefined {
    return this._version;
  }

  @Input() downloadModel?: () => void;
  @Input() downloadExcelModel?: () => void;
  @Input() refresh?: () => void;
  @Input() meeting?: boolean;

  ppm?: WithMetadata<Ppm>;
  status = '';
  controlBlocks?: DynamicBlocks[];
  metadataBlocks: DynamicBlocks[] = [];

  /** ── State ─────────────────────────────────────────────────────────── */
  readonly loading$ = new BehaviorSubject<boolean>(true);
  specialCommitteeOptions: SelectOption[] = [];
  acOptions: SelectOption[] = [];
  formObject: DynamicForm = getPpmInspectPlanFormObject({
    store: this.ppmRepository,
    acOptions: this.acOptions,
    download: (file: FileRepresentation) => this.fileService.download(file, NAMESPACE_ENUM.PPM),
    openFile: (file: FileRepresentation) => this.fileService.openFile(file, NAMESPACE_ENUM.PPM),
  });

  /** Track subscriptions to clean up */
  private sub?: Subscription;

  /** ── Lifecycle ────────────────────────────────────────────────────── */
  ngOnInit(): void {
    this.tryLoadData();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.multiFormAccordionService.reset(0);
    this.ppmRepository.reset();
  }

  /** ── Helpers ──────────────────────────────────────────────────────── */
  /**
   * Load data only when we have an id.
   * If a version is provided it will be used, otherwise the latest PPM is fetched.
   */
  private tryLoadData(): void {
    if (this._id === undefined) return;
    this.loadData();
  }

  private loadData(): void {
    this.loading$.next(true);
    this.multiFormAccordionService.reset(0);
    this.multiFormAccordionService.formFactory = (id: string) => ppmMilestonesStoreFactory(id);
    this.ppmMilestonesService.initialize();

    this.sub?.unsubscribe(); // avoid multiple subscriptions
    this.sub = combineLatest({
      acWithNoPpm: this.ppmService.getAcWithNoPpm().pipe(catchError(() => of([]))),
      allAc: this.contractingAuthorityService.getCurrentUserContractingAuthority().pipe(catchError(() => of([]))),
      ppm: this._version ? this.ppmService.getPpmVersion(this._id!, this._version) : this.ppmService.getPpm(this._id!),
      specialCommittees: this.committeeService.getAllSpecialCommittees().pipe(catchError(() => of([]))),
    }).subscribe(({ acWithNoPpm, allAc, ppm, specialCommittees }) => {
      this.ppm = ppm;
      this.status = ppm.status;
      this.metadataBlocks = this.ppmMetadataBlocksService.getMetadataBlocks({ pvItems: getPPMPVItems(ppm), ppmOpinions: ppm.avis });

      const controlBlocksParams = {
        refreshCount: ppm.nbrActualisation,
        status: ppm.status,
        accessHistory: () => this.router.navigate(['/ppm-version-history', this._id]),
        downloadSigned: ppm.modelAlfrescoId ? () => this.downloadModel?.() : undefined,
        downloadExcelModel: () => this.downloadExcelModel?.(),
      };
      this.controlBlocks = this.meeting
        ? this.ppmWorkflowBlocksService.getMeetingPpmBlocks(controlBlocksParams)
        : this.ppmWorkflowBlocksService.getInspectPpmBlocks({ ...controlBlocksParams, refresh: () => this.refresh?.() });
      // Map committees to select options
      this.specialCommitteeOptions = specialCommittees.map(c => ({
        name: c.nom,
        code: c.id,
      }));

      this.ppmMilestonesService.setMilestoneDtos(ppm.realisations || []);
      this.ppmRepository.setResponseAndDto(ppm);

      const selectedAc = allAc.find(ac => ac.id === this.ppmRepository.getNested('dto', 'acId'));
      this.acOptions = [
        ...(selectedAc ? [{ name: selectedAc.nom, code: selectedAc.id }] : []),
        ...acWithNoPpm.map(ac => ({ name: ac.acName, code: ac.acId })),
      ];

      this.formObject = getPpmInspectPlanFormObject({
        store: this.ppmRepository,
        acOptions: this.acOptions,
        openFile: (file: FileRepresentation) => this.fileService.openFile(file, NAMESPACE_ENUM.PPM),
      });

      this.loading$.next(false);
    });
  }
}
